import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, DollarSign, Loader2, Save, TrendingUp,
} from "lucide-react";
import PayPalCredentialsCard from "@/components/platform/PayPalCredentialsCard";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const GOLD_INK = "var(--brand-accent-ink)";
const SIM_EVENTS = [
  "PAYMENT.SALE.COMPLETED", "PAYMENT.SALE.DENIED",
  "BILLING.SUBSCRIPTION.ACTIVATED", "BILLING.SUBSCRIPTION.CANCELLED",
  "BILLING.SUBSCRIPTION.SUSPENDED",
];
const STATE_STYLE = {
  paid: "bg-teal-50 border-teal-200 text-teal-700",
  comped: "bg-purple-50 border-purple-200 text-purple-700",
  trial: "bg-blue-50 border-blue-200 text-blue-700",
  past_due: "bg-orange-50 border-orange-200 text-orange-700",
  suspended: "bg-red-50 border-red-200 text-red-700",
  cancelled: "bg-slate-100 border-slate-200 text-slate-500",
  cancelling: "bg-orange-50 border-orange-200 text-orange-700",
  active_unbilled: "bg-slate-50 border-slate-200 text-slate-500",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB",
  { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function RevenuePanel() {
  const [overview, setOverview] = useState(null);
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState(null);
  const [bandsEdit, setBandsEdit] = useState([]);
  const [savingPrices, setSavingPrices] = useState(false);
  const [simTenant, setSimTenant] = useState("");
  const [simBusy, setSimBusy] = useState(null);

  const load = () => {
    platformApi.get("/billing/overview").then((r) => {
      setOverview(r.data);
      setBandsEdit(r.data.pricing.map((p) => ({
        id: p.id, name: p.name, contact: !!p.contact, anchor_qar: p.anchor_qar,
        cap: p.employee_cap ?? "", qar: p.qar ?? "", usd: p.usd ?? "",
      })));
    });
    platformApi.get("/billing/config").then((r) => setConfig(r.data));
    platformApi.get("/billing/events?limit=50").then((r) => setEvents(r.data.events));
  };
  useEffect(load, []);

  const setBandField = (id, field) => (e) =>
    setBandsEdit((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: e.target.value } : r)));

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const bands = bandsEdit.map((r) => (r.contact
        ? { id: r.id, name: r.name, contact: true, anchor_qar: r.anchor_qar }
        : { id: r.id, name: r.name, cap: Number(r.cap), qar: Number(r.qar), usd: Number(r.usd) }));
      await platformApi.put("/billing/config", { bands });
      toast.success("Band pricing updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPrices(false);
    }
  };

  const simulate = async (eventType) => {
    if (!simTenant) { toast.error("Pick a tenant to simulate against"); return; }
    setSimBusy(eventType);
    try {
      const { data } = await platformApi.post("/billing/simulate-event",
        { event_type: eventType, subdomain: simTenant });
      toast.success(`Simulated ${eventType} → tenant status: ${data.tenant_status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSimBusy(null);
    }
  };

  if (!overview || !config) return <Skeleton className="h-96 w-full rounded-none" />;
  const maxPlanCount = Math.max(1, ...Object.values(overview.plan_distribution));

  return (
    <div className="space-y-6" data-testid="revenue-panel">
      {/* gateway credentials + connection */}
      <PayPalCredentialsCard config={config} onChanged={load} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["MRR (USD)", `$${overview.mrr_usd.toLocaleString()}`, "revenue-kpi-mrr-usd"],
          ["Tenants", overview.tenants.length, "revenue-kpi-tenants"],
          ["Paid subscriptions", overview.funnel.subscribed_paid, "revenue-kpi-paid"],
          ["Past due", overview.funnel.past_due, "revenue-kpi-pastdue"],
        ].map(([label, value, id]) => (
          <div key={id} className="bg-white border border-slate-200 px-5 py-4" data-testid={id}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
            <p className="text-[22px] font-bold font-mono mt-1" style={{ color: "var(--brand-primary)" }}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* plan distribution */}
        <div className="bg-white border border-slate-200 p-5" data-testid="plan-distribution">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-2"
            style={{ color: "var(--brand-primary)" }}>
            <TrendingUp className="h-4 w-4" style={{ color: GOLD_INK }} /> Plan distribution
          </p>
          <div className="mt-4 space-y-3">
            {Object.entries(overview.plan_distribution).map(([plan, count]) => (
              <div key={plan}>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="text-slate-600 font-medium">
                    {overview.pricing.find((p) => p.id === plan)?.name || plan}
                  </span>
                  <span className="font-mono text-slate-400">{count}</span>
                </div>
                <div className="h-2 bg-slate-100">
                  <div className="h-full" data-testid={`plan-bar-${plan}`}
                    style={{ width: `${(count / maxPlanCount) * 100}%`, background: "var(--brand-accent)" }} />
                </div>
              </div>
            ))}
          </div>
          {/* funnel */}
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] mt-6"
            style={{ color: "var(--brand-primary)" }}>Trial conversion funnel</p>
          <div className="mt-3 grid grid-cols-4 gap-2 text-center" data-testid="conversion-funnel">
            {[
              ["Signups", overview.funnel.signups],
              ["Trials now", overview.funnel.trials_now],
              ["Subscribed", overview.funnel.subscribed_paid],
              ["Comped", overview.funnel.comped],
            ].map(([label, value]) => (
              <div key={label} className="border border-slate-100 py-2.5">
                <p className="text-[16px] font-bold font-mono" style={{ color: "var(--brand-primary)" }}>{value}</p>
                <p className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* band pricing editor */}
        <div className="bg-white border border-slate-200 p-5" data-testid="pricing-editor">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-2"
            style={{ color: "var(--brand-primary)" }}>
            <DollarSign className="h-4 w-4" style={{ color: GOLD_INK }} /> Band pricing
          </p>
          <p className="text-[11px] text-slate-400 mt-1.5">
            User-facing prices are in USD. QAR is an optional secondary value kept
            for future regional display — never shown to prospects or tenants.
          </p>
          <div className="mt-3 grid grid-cols-[7rem_5rem_6rem_6rem] gap-x-3 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
            <span>Band</span><span>Cap</span><span>USD / mo</span><span>QAR (optional)</span>
          </div>
          <div className="mt-1.5 space-y-1.5">
            {bandsEdit.map((r) => (
              <div key={r.id} className="grid grid-cols-[7rem_5rem_6rem_6rem] gap-x-3 items-center">
                <span className="text-[12px] text-slate-600 font-medium truncate">{r.name}</span>
                {r.contact ? (
                  <span className="col-span-3 text-[11px] font-mono text-slate-400">contact-only band (no self-serve price)</span>
                ) : (
                  <>
                    <Input type="number" value={r.cap} data-testid={`cap-input-${r.id}`}
                      onChange={setBandField(r.id, "cap")}
                      className="rounded-none h-7 font-mono text-[12px]" />
                    <Input type="number" value={r.usd} data-testid={`price-input-${r.id}`}
                      onChange={setBandField(r.id, "usd")}
                      className="rounded-none h-7 font-mono text-[12px]" />
                    <Input type="number" value={r.qar} data-testid={`qar-input-${r.id}`}
                      onChange={setBandField(r.id, "qar")}
                      className="rounded-none h-7 font-mono text-[12px] text-slate-400" />
                  </>
                )}
              </div>
            ))}
          </div>
          <Button onClick={savePrices} disabled={savingPrices} data-testid="pricing-save-btn"
            className="rounded-none mt-4 h-8 text-[11px] bg-[#1E293B] hover:bg-slate-700 text-white">
            {savingPrices ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save band table
          </Button>
        </div>
      </div>

      {/* per-tenant billing table */}
      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="billing-tenants-table">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
              {["Tenant", "Plan", "Billing", "Subscription", "Next billing", "MRR (USD)"].map((h) => (
                <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {overview.tenants.map((t) => (
              <tr key={t.subdomain} data-testid={`billing-tenant-row-${t.subdomain}`}>
                <td className="px-4 py-2.5 font-mono font-semibold" style={{ color: "var(--brand-primary)" }}>
                  {t.subdomain}{t.internal ? <span className="ml-1.5 text-[9px] text-slate-400 font-normal">internal</span> : ""}
                </td>
                <td className="px-4 py-2.5 text-slate-500 capitalize">{t.plan}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${STATE_STYLE[t.billing_state] || ""}`}>
                    {t.billing_state.replace("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{t.subscription_id || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">
                  {t.billing_state === "past_due" ? `grace → ${fmtDate(t.grace_ends_at)}` : fmtDate(t.next_billing_time || t.period_end)}
                </td>
                <td className="px-4 py-2.5 font-mono">{t.mrr_usd ? `$${t.mrr_usd.toLocaleString()}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* events feed + simulator */}
      <div className="bg-white border border-slate-200" data-testid="billing-events-feed">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-2"
            style={{ color: "var(--brand-primary)" }}>
            <Activity className="h-4 w-4" style={{ color: GOLD_INK }} /> Billing events
          </p>
          <div className="flex items-center gap-2 flex-wrap" data-testid="event-simulator">
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-amber-50 border-amber-200 text-amber-700">
              sandbox simulation
            </span>
            <Select value={simTenant} onValueChange={setSimTenant}>
              <SelectTrigger className="rounded-none h-8 w-40 text-[11px]" data-testid="sim-tenant-select">
                <SelectValue placeholder="Pick tenant…" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {overview.tenants.filter((t) => t.subdomain !== "vertex").map((t) => (
                  <SelectItem key={t.subdomain} value={t.subdomain} className="text-[12px]"
                    data-testid={`sim-tenant-${t.subdomain}`}>
                    {t.subdomain}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {SIM_EVENTS.map((e) => (
              <button key={e} onClick={() => simulate(e)} disabled={simBusy !== null}
                data-testid={`simulate-${e.toLowerCase().replaceAll(".", "-")}`}
                className="text-[10px] font-mono border border-slate-300 px-2 py-1 hover:bg-slate-50 disabled:opacity-50">
                {simBusy === e ? "…" : e.split(".").slice(-1)[0]}
              </button>
            ))}
          </div>
        </div>
        {!events || events.length === 0 ? (
          <p className="text-[12px] text-slate-400 px-5 py-6" data-testid="billing-events-empty">
            No billing events yet — webhook deliveries and lifecycle events appear here.
          </p>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                {["Time", "Event", "Tenant", "Flags", "Summary"].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((ev) => (
                <tr key={ev.id} data-testid={`billing-event-${ev.id}`}>
                  <td className="px-4 py-2 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                    {ev.received_at ? new Date(ev.received_at).toLocaleString("en-GB") : "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px]" style={{ color: "var(--brand-primary)" }}>{ev.type}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{ev.tenant_id || "—"}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {ev.simulated && <span className="text-[9px] font-mono uppercase px-1 py-0.5 border bg-amber-50 border-amber-200 text-amber-700 mr-1">sim</span>}
                    {ev.verified
                      ? <CheckCircle2 className="h-3.5 w-3.5 inline" style={{ color: GOLD_INK }} />
                      : <span className="text-[9px] font-mono uppercase text-slate-400">unverified</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-500">{ev.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
